/**
 * @file src/pages/invoice-management-hub/components/InvoiceRow.jsx
 * @description Single row in the invoices table.
 *
 * Owns three pieces of local state:
 *  - showActions       — three-dots dropdown open/closed
 *  - showTemplateModal — template-picker modal (for View / Download PDF)
 *  - showAuditTrail    — audit trail modal
 *
 * Position-tracking for the dropdown: we capture the trigger button's
 * viewport-relative position so the dropdown can render fixed on top
 * of it. See ActionDropdown for the flip-upward logic.
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import StatusBadge, { PaymentStatusBadge } from "./StatusBadge";
import ActionDropdown from "./ActionDropdown";
import AuditTrailModal from "./AuditTrailModal";
import TemplateSelectionModal from "./TemplateSelectionModal";

const InvoiceRow = ({ invoice, isSelected, onSelect, index }) => {
  const [showActions, setShowActions] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateAction, setTemplateAction] = useState(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);

  const handleActionsClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setButtonPosition({
      top: rect.bottom,
      right: window.innerWidth - rect.right,
    });
    setShowActions(!showActions);
  };

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        className={`hover:bg-muted/50 transition-colors ${
          isSelected ? "bg-primary/10" : ""
        }`}
      >
        <td className="px-6 py-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(invoice.id)}
            className="rounded border-border"
          />
        </td>
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">
            {invoice.invoiceNumber}
          </div>
          <div className="text-sm text-muted-foreground">
            {invoice.hasQrCode ? "QR Code ✓" : "No QR Code"}
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">
            {invoice.buyer?.registrationName || "N/A"}
          </div>
          <div className="text-sm text-muted-foreground">
            VAT: {invoice.buyer?.vatNumber || "N/A"}
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="text-foreground">
            {new Date(invoice.issuedAt).toLocaleDateString("en-SA")}
          </div>
          <div className="text-sm text-muted-foreground">
            Created: {new Date(invoice.createdAt).toLocaleDateString("en-SA")}
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">
            SR{" "}
            {parseFloat(invoice.amount || 0).toLocaleString("en-SA", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Paid: SR{" "}
            {parseFloat(invoice.amountPaid || 0).toLocaleString("en-SA", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </td>
        <td className="px-6 py-4">
          <PaymentStatusBadge status={invoice.paymentStatus} />
          <div className="text-xs text-muted-foreground mt-1">
            Balance: SR{" "}
            {parseFloat(invoice.balanceDue || 0).toLocaleString("en-SA", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          {invoice.dueDate && (
            <div className="text-xs text-muted-foreground mt-1">
              Due: {new Date(invoice.dueDate).toLocaleDateString("en-SA")}
            </div>
          )}
        </td>
        <td className="px-6 py-4">
          <StatusBadge
            status={invoice.status}
            clearanceStatus={invoice.clearanceStaus}
            hasSignature={invoice.hasSignature}
          />
        </td>
        <td className="px-6 py-4 relative">
          <div className="relative">
            <button
              onClick={handleActionsClick}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="MoreVertical" size={16} />
            </button>
            <AnimatePresence>
              {showActions && (
                <ActionDropdown
                  invoice={invoice}
                  position={buttonPosition}
                  onClose={() => setShowActions(false)}
                  onShowTemplateModal={(action) => {
                    setTemplateAction(action);
                    setShowTemplateModal(true);
                    setShowActions(false);
                  }}
                  onShowAuditTrail={() => {
                    setShowActions(false);
                    setShowAuditTrail(true);
                  }}
                />
              )}
            </AnimatePresence>
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showTemplateModal && (
          <TemplateSelectionModal
            invoice={invoice}
            action={templateAction}
            onClose={() => setShowTemplateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAuditTrail && (
          <AuditTrailModal
            invoice={invoice}
            onClose={() => setShowAuditTrail(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default InvoiceRow;
